import {
  ACCESS_DENIED_ERROR,
  INTERNAL_SERVER_ERROR,
  LOGIN_MSG,
  NULL_ERROR,
  SIGNOUT_ERROR,
  UNAUTHORIZED_ERROR,
} from '@whistler-ui/k2/constants';
import {
  cacheApi,
  clearCache,
  removeQuotesFromText,
  signOutHelper,
  toHttpError,
} from '@whistler-ui/k2/helpers';
import { setLocalAppMsg } from '@whistler-ui/k2/hooks';
import { HttpError, SignOutType } from '@whistler-ui/k2/types';
import idx from 'idx';

import { API_ENDPOINTS } from '../constants';
import { routes } from '../routes';
import { AuthService } from './authService';
import { getBearerToken } from './authSession';

export const signOut = async (options?: SignOutType) => {
  const { appMsg, forceSignOut } = options || {};

  if (forceSignOut) {
    sessionStorage.clear();
    await AuthService.signOut({ global: false });
    return routes.login.url;
  }

  return signOutHelper(AuthService.signOut, routes.login.url, appMsg);
};

export const signupPage = async (options?: SignOutType) => {
  const { appMsg } = options || {};

  clearCache();
  sessionStorage.clear();

  if (appMsg) {
    setLocalAppMsg(appMsg);
  }

  await AuthService.signOut({ global: true }).catch(err =>
    console.error(SIGNOUT_ERROR, err),
  );
  return window.location.replace(routes.signup.url);
};

export const suspendedUser = async () => {
  clearCache();
  sessionStorage.clear();
  setLocalAppMsg(LOGIN_MSG.SUSPENDED_USER);
  await AuthService.signOut({ global: true }).catch(err =>
    console.error(SIGNOUT_ERROR, err),
  );
  return window.location.replace(routes.login.url);
};

const unauthorizedError = () => {
  throw new HttpError(UNAUTHORIZED_ERROR, 401, {
    userMessage: UNAUTHORIZED_ERROR,
  });
};

const handleNullErrors = async response => {
  let body = null;

  if (response.headers.get('content-type') === 'application/json') {
    body = await response.json();
  } else {
    body = await response.text();
  }

  if (body === null) {
    throw new Error(NULL_ERROR);
  }

  return body;
};

interface ErrorEnvelope {
  detail?: unknown;
  error_code?: string;
  error_id?: string;
}

type ErrorBody = ErrorEnvelope | unknown[] | null;

const hasDetail = (
  body: ErrorBody,
): body is ErrorEnvelope & { detail: unknown } => {
  return !Array.isArray(body) && Boolean(body?.detail);
};

const readErrorBody = async (response: Response): Promise<ErrorBody> => {
  try {
    return await response.json();
  } catch {
    // Non-JSON error bodies (proxy HTML pages, empty 502s) must not surface
    // a parser message to the user.
    return null;
  }
};

const buildErrorMessage = (body: ErrorBody): string => {
  if (hasDetail(body)) {
    return JSON.stringify(body.detail);
  }

  if (Array.isArray(body)) {
    const defaultErrorStructure = {
      detail: {
        msg: body.join(','),
        type: 'type_error.network_details',
      },
    };
    return JSON.stringify(defaultErrorStructure);
  }

  return INTERNAL_SERVER_ERROR;
};

/**
 * Only a 4xx `detail` is backend-authored copy meant for the user, so only
 * then is `userMessage` set. 5xx bodies stay in `message` for logs and
 * classifiers but are never shown (CWE-209).
 */
const handleDefaultErrors = async (response: Response) => {
  const body = await readErrorBody(response);
  const envelope = Array.isArray(body) ? null : body;
  const message = buildErrorMessage(body);
  const hasBackendDetail = hasDetail(body) || Array.isArray(body);
  const isClientError = response.status < 500;

  throw new HttpError(message, response.status, {
    userMessage:
      hasBackendDetail && isClientError
        ? removeQuotesFromText(message)
        : undefined,
    code: envelope?.error_code,
    errorId: envelope?.error_id,
  });
};

// switch statements are allowed to avoid complexity rule

const handleStatusCodeErrorsMiddleware = async (
  response: any,
  forceSignOut: any,
) => {
  switch (response.status) {
    case 401:
      if (forceSignOut) {
        signOut({ global: false });
        break;
      }
      return unauthorizedError();
    case 403:
      throw new HttpError(ACCESS_DENIED_ERROR, 403, {
        userMessage: ACCESS_DENIED_ERROR,
      });
    case 423:
      suspendedUser();
      break;
    case 500: {
      return await handleDefaultErrors(response);
    }
    case 204:
      return;
    case 200:
    case 201:
    case 202:
      return await handleNullErrors(response);
    default:
      return await handleDefaultErrors(response);
  }
};

export const requestWithOptions = async (
  path: any,
  { forceSignout = true, isMultipart = false, ...options }: any,
  cacheParams?: any,
) => {
  let capturedStatus: number | undefined;
  try {
    const currentUserSession = await AuthService.currentSession();
    const headers = idx(options, _ => _.headers) || {};

    // excluding the Content-Type property,
    //allowing the browser to detect and set the boundary and content type automatically.
    const contentType = isMultipart
      ? {}
      : { 'Content-Type': 'application/json' };

    return await cacheApi({
      path,
      params: cacheParams,
      options: {
        ...options,
        headers: {
          Authorization: getBearerToken(currentUserSession),
          Accept: '*/*',
          ...contentType,
          ...headers,
        },
      },
      responseHandler: async response => {
        capturedStatus = response.status;
        return handleStatusCodeErrorsMiddleware(response, forceSignout);
      },
    });
  } catch (e) {
    throw toHttpError(e, capturedStatus);
  }
};

export const requestWithOptionsNoAuth = async (
  path: any,
  options: any,
  cacheParams?: any,
) => {
  let capturedStatus: number | undefined;
  try {
    const headers = idx(options, _ => _.headers) || {};
    return await cacheApi({
      path,
      params: cacheParams,
      options: {
        ...options,
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          ...headers,
        },
      },
      responseHandler: async response => {
        capturedStatus = response.status;
        return await handleStatusCodeErrorsMiddleware(response, false);
      },
    });
  } catch (e) {
    throw toHttpError(e, capturedStatus);
  }
};

// Utility function to fetch with a timeout
const fetchWithTimeout = async (url: string, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const signal = controller.signal;

  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  try {
    const response = await fetch(url, { ...options, signal });
    clearTimeout(timeoutId); // Clear timeout if fetch succeeds
    return response;
  } catch (error) {
    clearTimeout(timeoutId); // Clear timeout on error
    throw error; // Re-throw other errors
  }
};

// Your service with timeout added
export const getInvalidEndpoints = async () => {
  const removeTrailingSlash = (url: string | undefined) => {
    if (!url) return '';
    // Removing `/` if it ends with `/`
    return url.endsWith('/') ? url.slice(0, -1) : url;
  };

  const requests = Object.entries(API_ENDPOINTS).map(async ([key, url]) => {
    return fetchWithTimeout(
      `${removeTrailingSlash(url)}/is-reachable`,
      { method: 'HEAD', mode: 'no-cors' },
      6000,
    )
      .then(response => {
        if (response.ok) {
          return { key, status: response.status };
        } else {
          return { key, status: 200 };
        }
      })
      .catch(() => {
        return { key, status: 500 };
      });
  });

  const results = await Promise.allSettled(requests);

  // Filter out results that were rejected or have a status of 500
  const urlWithErrors = results
    .filter(
      result => result.status === 'rejected' || result.value.status !== 200,
    )
    .map(result => {
      const key: keyof typeof API_ENDPOINTS =
        result.status === 'fulfilled' ? result.value.key : result.reason.key;
      const url = removeTrailingSlash(API_ENDPOINTS[key]);
      return {
        key,
        url,
      };
    });

  return urlWithErrors;
};
